/** QuickBooks Online integration (v1 qbo-expenses.js) over the ALREADY-DEPLOYED
 * backend (flipbase-clone/backend routes/qbo.py, /api/qbo). Connect is a browser
 * OAuth redirect; status / accounts / disconnect are reads/toggles; PUSH writes a
 * real expense into QuickBooks and is gated behind liveFire `qboSync` (OFF until
 * the supervised go-live). No backend changes — v2 UI onto live endpoints. */
import { API_BASE } from "../../config";
import { backendFetch } from "../../lib/backend";

export interface QboStatus {
  configured: boolean; // backend has QBO_CLIENT_ID/SECRET
  connected: boolean; // an OAuth token is live
  company: string; // connected company name (when known)
  warn: string; // non-fatal backend warning
}
export interface QboAccount { id: string; name: string; type?: string }
export interface QboAccounts { expense: QboAccount[]; payment: QboAccount[] }

/** Browser navigates here to start the OAuth handshake (no bearer needed — the
 * backend redirects to Intuit, then back to the app with ?qbo=connected). */
export const qboConnectUrl = (): string => API_BASE + "/api/qbo/connect";

const s = (v: unknown): string => (typeof v === "string" ? v : "");

export async function fetchQboStatus(token: string | null, signal?: AbortSignal): Promise<QboStatus> {
  const j = await backendFetch<Record<string, unknown>>(token, "/api/qbo/status", { timeoutMs: 30000, ...(signal ? { signal } : {}) });
  return { configured: j["configured"] === true, connected: j["connected"] === true, company: s(j["company"]), warn: s(j["warn"]) };
}

export async function fetchQboAccounts(token: string | null, signal?: AbortSignal): Promise<QboAccounts> {
  const j = await backendFetch<Record<string, unknown>>(token, "/api/qbo/accounts", { timeoutMs: 30000, ...(signal ? { signal } : {}) });
  const map = (arr: unknown): QboAccount[] =>
    (Array.isArray(arr) ? arr : []).map((a) => {
      const o = (a ?? {}) as Record<string, unknown>;
      return { id: s(o["id"]), name: s(o["name"]), ...(o["type"] !== undefined ? { type: s(o["type"]) } : {}) };
    }).filter((a) => a.id);
  return { expense: map(j["expense"]), payment: map(j["payment"]) };
}

export async function qboDisconnect(token: string | null): Promise<void> {
  await backendFetch(token, "/api/qbo/disconnect", { method: "POST", body: {}, timeoutMs: 30000 });
}

export interface QboPushExpense {
  crm_id: string;
  date: string; // YYYY-MM-DD
  desc: string;
  category: string;
  amount: number; // subtotal + tax
  payment?: string;
  vendor?: string;
  property?: string; // deal address, or "" for a company expense
  qbo_id?: string; // set → update instead of create
  pay_account_id?: string;
  fallback_account_id?: string;
}

/** GATED — caller must check liveOn("qboSync") first. Returns the QBO purchase id. */
export async function qboPush(token: string | null, req: QboPushExpense): Promise<{ ok: boolean; qbo_id: string }> {
  const j = await backendFetch<Record<string, unknown>>(token, "/api/qbo/push", { method: "POST", body: req, timeoutMs: 45000 });
  return { ok: j["ok"] === true, qbo_id: s(j["qbo_id"]) };
}

/** Detect the OAuth round-trip result the backend appends to the app URL
 * (?qbo=connected | ?qbo=error) so the page can refresh + clean the query. */
export function readQboReturn(): "connected" | "error" | null {
  try {
    const p = new URLSearchParams(window.location.search).get("qbo");
    return p === "connected" || p === "error" ? p : null;
  } catch {
    return null;
  }
}
